import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { CheckSquare, Plus, Search, Calendar as CalendarIcon, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import UserAvatar from '../components/UserAvatar';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EOSTodos = () => {
  const [todos, setTodos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('open');
  const [currentUser, setCurrentUser] = useState(null);
  const [viewMode, setViewMode] = useState('my'); // 'my' or 'all'

  // Get user info from localStorage
  const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = ['super_admin', 'admin', 'ceo', 'president'].includes(storedUser.role);

  // Form state
  const [todoForm, setTodoForm] = useState({
    task: '',
    description: '',
    owner_id: '',
    due_date: '',
    priority: 'medium'
  });

  useEffect(() => {
    fetchTodos();
    fetchUsers();
    fetchCurrentUser();
  }, []);

  useEffect(() => {
    fetchTodos();
  }, [searchQuery, filterStatus, viewMode]);

  const fetchTodos = async () => {
    try {
      const params = {};
      if (filterStatus !== 'all') params.status = filterStatus;
      
      // Filter by owner if "My To-Dos" mode
      if (viewMode === 'my') {
        params.owner_id = storedUser.user_id || storedUser.id;
      }

      const response = await axios.get(`${API}/eos/todos`, { params });
      let filteredTodos = response.data;

      if (searchQuery) {
        filteredTodos = filteredTodos.filter(todo =>
          todo.task.toLowerCase().includes(searchQuery.toLowerCase()) ||
          (todo.description && todo.description.toLowerCase().includes(searchQuery.toLowerCase()))
        );
      }

      setTodos(filteredTodos);
    } catch (error) {
      console.error('Error fetching todos:', error);
      toast.error('Failed to load to-dos');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchCurrentUser = async () => {
    try {
      const response = await axios.get(`${API}/users/me`);
      setCurrentUser(response.data);
    } catch (error) {
      console.error('Error fetching current user:', error);
    }
  };

  const handleCreateTodo = async () => {
    try {
      if (!todoForm.task || !todoForm.owner_id || !todoForm.due_date) {
        toast.error('Please fill in task, owner, and due date');
        return;
      }

      const payload = {
        ...todoForm,
        due_date: new Date(todoForm.due_date).toISOString()
      };

      await axios.post(`${API}/eos/todos`, payload);
      toast.success('To-Do created successfully');
      setCreateDialogOpen(false);
      resetTodoForm();
      fetchTodos();
    } catch (error) {
      console.error('Error creating todo:', error);
      toast.error(error.response?.data?.detail || 'Failed to create to-do');
    }
  };

  const handleCompleteTodo = async (todoId) => {
    try {
      await axios.patch(`${API}/eos/todos/${todoId}/complete`);
      toast.success('To-Do marked as complete');
      fetchTodos();
    } catch (error) {
      console.error('Error completing todo:', error);
      toast.error('Failed to complete to-do');
    }
  };

  const resetTodoForm = () => {
    setTodoForm({
      task: '',
      description: '',
      owner_id: '',
      due_date: '',
      priority: 'medium'
    });
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-500';
      case 'medium': return 'bg-yellow-500';
      case 'low': return 'bg-green-500';
      default: return 'bg-gray-400';
    }
  };

  const isOverdue = (dueDate) => {
    return new Date(dueDate) < new Date() && new Date(dueDate).toDateString() !== new Date().toDateString();
  };

  const isDueToday = (dueDate) => {
    return new Date(dueDate).toDateString() === new Date().toDateString();
  };

  const isDueThisWeek = (dueDate) => {
    const today = new Date();
    const due = new Date(dueDate);
    const weekFromNow = new Date();
    weekFromNow.setDate(today.getDate() + 7);
    return due >= today && due <= weekFromNow;
  };

  const formatDueDate = (dueDate) => {
    const due = new Date(dueDate);
    const today = new Date();
    const diffTime = due - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) {
      return `${Math.abs(diffDays)} days overdue`;
    } else if (diffDays === 0) {
      return 'Due today';
    } else if (diffDays === 1) {
      return 'Due tomorrow';
    } else if (diffDays <= 7) {
      return `Due in ${diffDays} days`;
    } else {
      return due.toLocaleDateString();
    }
  };

  // Categorize todos
  const overdueTodos = todos.filter(t => t.status === 'open' && isOverdue(t.due_date));
  const todayTodos = todos.filter(t => t.status === 'open' && isDueToday(t.due_date));
  const thisWeekTodos = todos.filter(t => t.status === 'open' && isDueThisWeek(t.due_date) && !isDueToday(t.due_date));
  const laterTodos = todos.filter(t => t.status === 'open' && !isOverdue(t.due_date) && !isDueToday(t.due_date) && !isDueThisWeek(t.due_date));
  const completedTodos = todos.filter(t => t.status === 'complete');

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <p className="mt-4 text-muted-foreground">Loading To-Dos...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CheckSquare className="h-8 w-8 text-blue-600" />
            <div>
              <h1 className="text-3xl font-bold">To-Dos</h1>
              <p className="text-sm text-muted-foreground">
                Track action items and accountability
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* View Mode Toggle */}
            <div className="flex rounded-lg border border-slate-300 dark:border-slate-700 overflow-hidden">
              <button
                onClick={() => setViewMode('my')}
                className={`px-3 py-1.5 text-sm transition-colors ${
                  viewMode === 'my' 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                }`}
              >
                My To-Dos
              </button>
              {isAdmin && (
                <button
                  onClick={() => setViewMode('all')}
                  className={`px-3 py-1.5 text-sm transition-colors ${
                    viewMode === 'all' 
                      ? 'bg-blue-600 text-white' 
                      : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                  }`}
                >
                  All To-Dos
                </button>
              )}
            </div>
            <Button onClick={() => setCreateDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              New To-Do
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="text-center">
                <div className="text-3xl font-bold">{todos.filter(t => t.status === 'open').length}</div>
                <div className="text-sm text-muted-foreground">Open</div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-red-600">{overdueTodos.length}</div>
                <div className="text-sm text-muted-foreground">Overdue</div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-orange-600">{todayTodos.length}</div>
                <div className="text-sm text-muted-foreground">Due Today</div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-blue-600">{thisWeekTodos.length}</div>
                <div className="text-sm text-muted-foreground">This Week</div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-green-600">{completedTodos.length}</div>
                <div className="text-sm text-muted-foreground">Complete</div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search and Filters */}
        <div className="flex gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search to-dos..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-[150px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="open">Open</SelectItem>
              <SelectItem value="complete">Complete</SelectItem>
              <SelectItem value="all">All</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Overdue To-Dos */}
        {overdueTodos.length > 0 && (
          <Card className="border-red-500">
            <CardHeader>
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-6 w-6 text-red-600" />
                <div>
                  <CardTitle className="text-red-600">Overdue</CardTitle>
                  <CardDescription>{overdueTodos.length} overdue to-dos</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {overdueTodos.map(todo => (
                  <div 
                    key={todo.id} 
                    className="flex items-start gap-3 p-4 rounded-lg border border-red-200 bg-red-50 dark:bg-red-950"
                  >
                    <input
                      type="checkbox"
                      className="mt-1 h-5 w-5 cursor-pointer"
                      onChange={() => handleCompleteTodo(todo.id)}
                    />
                    <div className="flex-1">
                      <div className="font-semibold">{todo.task}</div>
                      {todo.description && (
                        <p className="text-sm text-muted-foreground mt-1">{todo.description}</p>
                      )}
                      <div className="flex items-center gap-4 mt-2 text-sm">
                        <span className="text-red-600 font-medium">
                          ⚠️ {formatDueDate(todo.due_date)}
                        </span>
                        <span className="text-muted-foreground">
                          Owner: {todo.owner_details 
                            ? `${todo.owner_details.first_name} ${todo.owner_details.last_name}`
                            : 'N/A'}
                        </span>
                        <div className={`w-2 h-2 rounded-full ${getPriorityColor(todo.priority)}`}></div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Due Today */}
        {todayTodos.length > 0 && (
          <Card className="border-orange-500">
            <CardHeader>
              <div className="flex items-center gap-2">
                <CalendarIcon className="h-6 w-6 text-orange-600" />
                <div>
                  <CardTitle className="text-orange-600">Due Today</CardTitle>
                  <CardDescription>{todayTodos.length} to-dos due today</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {todayTodos.map(todo => (
                  <div 
                    key={todo.id} 
                    className="flex items-start gap-3 p-4 rounded-lg border border-orange-200 bg-orange-50 dark:bg-orange-950"
                  >
                    <input
                      type="checkbox"
                      className="mt-1 h-5 w-5 cursor-pointer"
                      onChange={() => handleCompleteTodo(todo.id)}
                    />
                    <div className="flex-1">
                      <div className="font-semibold">{todo.task}</div>
                      {todo.description && (
                        <p className="text-sm text-muted-foreground mt-1">{todo.description}</p>
                      )}
                      <div className="flex items-center gap-4 mt-2 text-sm">
                        <span className="text-orange-600 font-medium">📅 Due today</span>
                        <span className="text-muted-foreground">
                          Owner: {todo.owner_details 
                            ? `${todo.owner_details.first_name} ${todo.owner_details.last_name}`
                            : 'N/A'}
                        </span>
                        <div className={`w-2 h-2 rounded-full ${getPriorityColor(todo.priority)}`}></div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Due This Week */}
        {thisWeekTodos.length > 0 && (
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <CalendarIcon className="h-6 w-6 text-blue-600" />
                <div>
                  <CardTitle>Due This Week</CardTitle>
                  <CardDescription>{thisWeekTodos.length} to-dos due this week</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {thisWeekTodos.map(todo => (
                  <div 
                    key={todo.id} 
                    className="flex items-start gap-3 p-4 rounded-lg border hover:bg-accent transition-colors"
                  >
                    <input
                      type="checkbox"
                      className="mt-1 h-5 w-5 cursor-pointer"
                      onChange={() => handleCompleteTodo(todo.id)}
                    />
                    <div className="flex-1">
                      <div className="font-semibold">{todo.task}</div>
                      {todo.description && (
                        <p className="text-sm text-muted-foreground mt-1">{todo.description}</p>
                      )}
                      <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                        <span>📅 {formatDueDate(todo.due_date)}</span>
                        <span>
                          Owner: {todo.owner_details 
                            ? `${todo.owner_details.first_name} ${todo.owner_details.last_name}`
                            : 'N/A'}
                        </span>
                        <div className={`w-2 h-2 rounded-full ${getPriorityColor(todo.priority)}`}></div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Due Later */}
        {laterTodos.length > 0 && (
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <CheckSquare className="h-6 w-6 text-gray-600" />
                <div>
                  <CardTitle>Due Later</CardTitle>
                  <CardDescription>{laterTodos.length} to-dos</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {laterTodos.map(todo => (
                  <div 
                    key={todo.id} 
                    className="flex items-start gap-3 p-4 rounded-lg border hover:bg-accent transition-colors"
                  >
                    <input
                      type="checkbox"
                      className="mt-1 h-5 w-5 cursor-pointer"
                      onChange={() => handleCompleteTodo(todo.id)}
                    />
                    <div className="flex-1">
                      <div className="font-semibold">{todo.task}</div>
                      {todo.description && (
                        <p className="text-sm text-muted-foreground mt-1">{todo.description}</p>
                      )}
                      <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                        <span>📅 {formatDueDate(todo.due_date)}</span>
                        <span>
                          Owner: {todo.owner_details 
                            ? `${todo.owner_details.first_name} ${todo.owner_details.last_name}`
                            : 'N/A'}
                        </span>
                        <div className={`w-2 h-2 rounded-full ${getPriorityColor(todo.priority)}`}></div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Recently Completed */}
        {completedTodos.length > 0 && filterStatus !== 'open' && (
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-6 w-6 text-green-600" />
                <div>
                  <CardTitle>Recently Completed</CardTitle>
                  <CardDescription>{completedTodos.length} completed to-dos</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {completedTodos.slice(0, 10).map(todo => (
                  <div 
                    key={todo.id} 
                    className="flex items-start gap-3 p-3 rounded-lg bg-green-50 dark:bg-green-950"
                  >
                    <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5" />
                    <div className="flex-1">
                      <div className="font-medium line-through text-muted-foreground">{todo.task}</div>
                      <div className="text-xs text-muted-foreground mt-1">
                        Completed {new Date(todo.completed_at).toLocaleDateString()} by{' '}
                        {todo.owner_details 
                          ? `${todo.owner_details.first_name} ${todo.owner_details.last_name}`
                          : 'N/A'}
                      </div>
                    </div>
                  </div>
                ))}
                {completedTodos.length > 10 && (
                  <p className="text-sm text-muted-foreground text-center pt-2">
                    +{completedTodos.length - 10} more completed to-dos
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Empty State */}
        {todos.length === 0 && (
          <Card>
            <CardContent className="text-center py-12">
              <CheckSquare className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground mb-4">No to-dos yet. Create your first to-do!</p>
              <Button onClick={() => setCreateDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                New To-Do
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Create To-Do Dialog */}
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create New To-Do</DialogTitle>
              <DialogDescription>Add an action item with a deadline</DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div>
                <Label>Task *</Label>
                <Input
                  placeholder="e.g., Schedule Q1 planning session"
                  value={todoForm.task}
                  onChange={(e) => setTodoForm({ ...todoForm, task: e.target.value })}
                />
              </div>

              <div>
                <Label>Description (Optional)</Label>
                <Textarea
                  placeholder="Add more details about this to-do..."
                  value={todoForm.description}
                  onChange={(e) => setTodoForm({ ...todoForm, description: e.target.value })}
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Owner *</Label>
                  <Select 
                    value={todoForm.owner_id}
                    onValueChange={(value) => setTodoForm({ ...todoForm, owner_id: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select owner" />
                    </SelectTrigger>
                    <SelectContent>
                      {users.map(user => (
                        <SelectItem key={user.user_id} value={user.user_id}>
                          {user.first_name} {user.last_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Due Date *</Label>
                  <Input
                    type="date"
                    value={todoForm.due_date}
                    onChange={(e) => setTodoForm({ ...todoForm, due_date: e.target.value })}
                  />
                </div>
              </div>

              <div>
                <Label>Priority</Label>
                <Select 
                  value={todoForm.priority}
                  onValueChange={(value) => setTodoForm({ ...todoForm, priority: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="high">🔴 High Priority</SelectItem>
                    <SelectItem value="medium">🟡 Medium Priority</SelectItem>
                    <SelectItem value="low">🟢 Low Priority</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => {
                setCreateDialogOpen(false);
                resetTodoForm();
              }}>
                Cancel
              </Button>
              <Button onClick={handleCreateTodo}>Create To-Do</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default EOSTodos;
